/**
 * Sealed writes: the request half of the annotations seam (spec
 * `docs/plans/2026-09-02-next-steps-e2e-encryption.md`, sections 6.3 to 6.5;
 * API contract sections 2 and 3).
 *
 * `courses-annotations-transport.js` is the one door every note, highlight,
 * personal tag and blank answer passes through. This module installs its
 * REQUEST hook, so an enrolled member's writes leave the device as envelopes
 * and the `courses` store keeps building plaintext bodies exactly as it always
 * has. The response hook — opening envelopes on the way back — belongs to the
 * read path (#1542) and is deliberately untouched here: `setAnnotationTransportHooks`
 * merges the side it is given, so the two attach independently and in either
 * order.
 *
 * WHAT GOES OUT, per entity (contract section 3's table):
 *
 *   note       payload + a client `id` on a create; never `title` or `body`
 *   highlight  payload + a client `id` on a create; never a position field
 *   tag        payload + `nameIndex` + a client `id` on a create; never `name`
 *   blank      payload; an EMPTIED blank deletes the row instead. Both carry
 *              `expectedRevision` when this device has read the row — never
 *              required, because an upsert's first write has no row to name one
 *              for, and the delete carries it as a query parameter
 *
 * Everything else the store sent — `programSlug`, `lessonSlug`, `highlightId`,
 * `view`, `tagIds`, `blockId` — travels in the clear, because the server routes,
 * orders and counts on it. A field that is neither sealed nor listed by the
 * contract as forbidden is passed through untouched rather than dropped: this
 * module must never be what silently loses a field a later API version adds.
 *
 * THE CLIENT ID. An envelope's AAD names the row before the server has stored
 * it (`"<memberId>:<type>:<itemId>"`), so a create has to know its own id.
 * The store does not mint one, and the seam reports `itemId: null` for a body
 * without an `id` — that null IS how a create is recognised here, and the id is
 * minted at this seam and sent with the body. The API accepts it (`@IsUUID('4')`)
 * and answers 409 on a collision.
 *
 * A PARTIAL PATCH IS REFUSED, NOT NARROWED. A `payload` REPLACES the row's
 * payload column, so an update seals whatever content fields the body carries
 * and every field it does not carry is destroyed — the server nulls the
 * plaintext columns, so there is nothing left to restore from. Three real
 * shapes did exactly that before this guard existed, and none of them was a
 * misuse of the seam:
 *
 *   1. the highlight heal write-back (`courses.js` `fetchLessonHighlights`)
 *      sent four corrected anchors and destroyed the quote, its context, the
 *      colour and the page index — the quote being the only thing that can
 *      ever re-find the mark again;
 *   2. a colour-only highlight PATCH sent no `pageIndex`, and no quote at all
 *      when the reader had none to offer;
 *   3. a tag rename sent `name` alone, because that is what both callers
 *      dispatch, and destroyed the tag's colour.
 *
 * So `REQUIRED_ON_UPDATE` names, per type, the content fields an update must
 * carry, and an update short of them throws rather than writing a lossy
 * envelope: a refused write the caller can toast is recoverable, a replaced
 * payload is not. The store now sends the whole content set at each of those
 * three call sites, so the guard is a backstop against the next caller rather
 * than a live refusal. A CREATE is exempt — its envelope IS the whole row, and
 * a field the create legitimately has nothing for (no words before the span,
 * so no `contextBefore`) has nothing to lose.
 *
 * NOT ENROLLED IS A PASS-THROUGH — with two additions. A note UPDATE and a
 * blank write both carry `expectedRevision` when this device has read the row
 * this session (`plaintextRevision`). The API accepts the field on a plaintext
 * write and, once its plaintext branch honours it, refuses a stale one with the
 * same 409 an envelope gets — so a plaintext member's replay from the outbox, or
 * their second device, stops overwriting an edit made since. What happens then
 * is decided by where the write came from: the row they are typing in refetches
 * and forces through, a replay yields (`courses.js#forceThroughConflict`).
 *
 * And the pass-through is only for a member who really is not enrolled. A member
 * the API considers enrolled whose device holds no key
 * (enrolment 201'd and the persist failed, IndexedDB was evicted, this device
 * is PENDING approval) must NOT fall through to plaintext: the API would
 * refuse it 400 anyway, and the note body would have left the device in the
 * clear on the way to being refused. That case throws NO_KEY instead, which
 * keeps the plaintext on the device and gives the approval path a state to
 * react to. "Really not enrolled" is the API's word, not an absent snapshot's:
 * when nothing here knows yet, the hook reads the status once before deciding
 * (`apiSaysEnrolled`).
 */
import { UNRECOVERABLE_FIELD } from '@/constants/courses-encryption';
import {
  ANNOTATION_TYPE,
  DELETE_BLANK_ANSWER,
  setAnnotationTransportHooks,
} from '@/utils/courses-annotations-transport';
import { tagNameIndex } from '@/utils/courses-crypto/blind-index';
import { blankItemId, seal } from '@/utils/courses-crypto/envelope';
import { randomUuid } from '@/utils/courses-crypto/webcrypto';
import { revisionFor } from '@/utils/courses-encryption/annotation-revisions';
import { enrolmentInFlight } from '@/utils/courses-encryption/enrolment';
import { statusNeverRead } from '@/utils/courses-encryption/status-prime';

/**
 * What section 6.4 puts INSIDE each envelope. The order is the spec's, and the
 * spec's order is the contract's; a spec pins the whole map so a field cannot
 * be added to one renderer and forgotten here.
 */
export const SEALED_FIELDS = Object.freeze({
  [ANNOTATION_TYPE.NOTE]: Object.freeze(['title', 'body']),
  [ANNOTATION_TYPE.HIGHLIGHT]: Object.freeze([
    'pageIndex',
    'blockId',
    'endBlockId',
    'startWordIndex',
    'endWordIndex',
    'selectedText',
    'contextBefore',
    'contextAfter',
    'color',
  ]),
  [ANNOTATION_TYPE.TAG]: Object.freeze(['name', 'color']),
  [ANNOTATION_TYPE.BLANK]: Object.freeze(['answer']),
});

/**
 * What an UPDATE must carry for its envelope to hold the row's whole content
 * set. It is `SEALED_FIELDS` minus the fields this app never writes:
 *
 *   note   `title` is in the envelope's field list because section 6.4 puts it
 *          there, but nothing in this app has ever written one — no create and
 *          no PATCH sends it — so a note envelope holds `body` alone and a
 *          body-only PATCH loses nothing. A spec pins that no writer appears.
 *   blank  a blank answer is a PUT keyed on `program/lesson/block`, not a
 *          partial patch: an absent answer is a DELETE, so there is no shape
 *          to be short of.
 */
export const REQUIRED_ON_UPDATE = Object.freeze({
  [ANNOTATION_TYPE.NOTE]: Object.freeze(['body']),
  [ANNOTATION_TYPE.HIGHLIGHT]: SEALED_FIELDS[ANNOTATION_TYPE.HIGHLIGHT],
  [ANNOTATION_TYPE.TAG]: SEALED_FIELDS[ANNOTATION_TYPE.TAG],
  [ANNOTATION_TYPE.BLANK]: SEALED_FIELDS[ANNOTATION_TYPE.BLANK],
});

/**
 * The two `courses-keys` enrolment statuses that mean "the API considers this
 * member enrolled". Spelled out rather than imported: `coursesKeys.js` pulls in
 * the platform key adapter, which statically imports two native plugins, and
 * this module is installed at start-up by every build including the web one. A
 * spec pins these against `ENROLMENT_STATUS` so the two cannot drift.
 */
const ENROLLED_STATUSES = Object.freeze(['enrolled', 'pending']);

/** `courses-keys/hydrationStatus` while the key store has not been read yet. */
const HYDRATING = 'hydrating';

/** How long a write waits for an in-flight hydration before deciding. */
export const HYDRATION_WAIT_MS = 3000;

/**
 * After the encryption status could not be read, how long writes are refused
 * without asking again. Answers save on every keystroke; without this an
 * outage would cost two failing reads per keystroke.
 */
export const STATUS_READ_BACKOFF_MS = 10 * 1000;

export const SEALED_WRITE_ERRORS = Object.freeze({
  /** The transport does not know the member yet, so no AAD can be built. */
  NO_MEMBER: 'NO_MEMBER',
  /** The transport's member and the key's member disagree. */
  MEMBER_MISMATCH: 'MEMBER_MISMATCH',
  /** Enrolled, but this device holds no key: refuse rather than send plaintext. */
  NO_KEY: 'NO_KEY',
  /** A tag write with no name: its blind index cannot be computed. */
  NO_TAG_NAME: 'NO_TAG_NAME',
  /** An update carrying none of its entity's content fields. */
  EMPTY_PATCH: 'EMPTY_PATCH',
  /** An update carrying some but not all of them: sealing it would destroy the rest. */
  PARTIAL_PATCH: 'PARTIAL_PATCH',
  /** A blank answer whose program/lesson/block key is incomplete. */
  NO_BLANK_KEY: 'NO_BLANK_KEY',
  /**
   * A rotation is running and this device does not hold the pending
   * generation, so it cannot write at all until the rotation ends (#1694).
   *
   * NOT `NO_KEY`. This device may hold MK1 perfectly well and read every row
   * it has always read; what it cannot do is write one, because the API
   * refuses any `kid` but the pending one while a rotation is open
   * (api#1133). Reporting it as "no key" would send the member to the
   * approval or recovery screens for a device that needs neither.
   */
  ROTATION_IN_PROGRESS: 'ROTATION_IN_PROGRESS',
  /** A type the seam reported that this module has no rule for. */
  UNKNOWN_TYPE: 'UNKNOWN_TYPE',
  /**
   * An envelope update on a row whose revision this device has not read
   * (#1584). The API requires `expectedRevision` on every encrypted PATCH and
   * answers 400 without it, so this refusal is the same outcome reached one
   * round trip earlier — and with the member's edit still in hand.
   */
  NO_REVISION: 'NO_REVISION',
  /**
   * No key here and the encryption status could not be read, so whether
   * plaintext is allowed is not known. Refused rather than guessed; the blank
   * action keeps the answer on the device, unsent, the way it keeps one typed
   * offline.
   */
  STATUS_UNKNOWN: 'STATUS_UNKNOWN',
});

/**
 * What a member may be shown for each refusal. `SealedWriteError.message` adds
 * a developer detail after a colon; screens read this map by `code` instead.
 */
export const SEALED_WRITE_MESSAGES = Object.freeze({
  NO_MEMBER: 'This note cannot be encrypted until your profile has loaded',
  MEMBER_MISMATCH: 'This note belongs to a different member than the key on this device',
  NO_KEY: 'This device does not have your notes key yet, so nothing can be saved here',
  NO_TAG_NAME: 'A tag cannot be saved without a name once notes are encrypted',
  EMPTY_PATCH: 'This edit carried nothing to encrypt',
  PARTIAL_PATCH: 'This edit would have replaced the rest of what you saved',
  NO_BLANK_KEY: 'This answer cannot be encrypted without the lesson it belongs to',
  UNKNOWN_TYPE: 'This kind of note cannot be encrypted by this app version',
  NO_REVISION: 'This item has to be opened again before it can be saved',
  STATUS_UNKNOWN:
    'Could not check whether your notes are encrypted. Check your connection and try again',
});

export class SealedWriteError extends Error {
  /**
   * @param {keyof typeof SEALED_WRITE_ERRORS} code
   * @param {string} [detail]
   */
  constructor(code, detail) {
    super(detail ? `${SEALED_WRITE_MESSAGES[code]}: ${detail}` : SEALED_WRITE_MESSAGES[code]);
    this.name = 'SealedWriteError';
    this.code = code;
  }
}

/**
 * Whether an error is the hook refusing a write outright, as opposed to the
 * request failing. The store's write paths that keep a failed request cached
 * (an answer typed offline must survive) must not keep one of these: nothing
 * was sent and nothing will be.
 *
 * @param {unknown} error
 * @param {keyof typeof SEALED_WRITE_ERRORS} [code] narrow to one refusal
 * @returns {error is SealedWriteError}
 */
export function isSealedWriteError(error, code) {
  const sealed = error instanceof SealedWriteError || error?.name === 'SealedWriteError';
  return sealed && (code === undefined || error.code === code);
}

/**
 * The API's code for "this account is encrypted: send an envelope, not
 * plaintext" (`EncryptionModeGuard`, api#1164 / `6ee5c2e4`).
 *
 * BRANCHES ON THE CODE, NOT THE MESSAGE, and that is not a preference — the
 * contract's own error vocabulary says a client must branch on `code` and treat
 * `message` as display-only, and it says so because of us: mobile#1572 told a
 * retryable stale nonce from a rejected signature by exact-matching English
 * prose, and a reword silently dropped the retry. This row was matched on the
 * sentence for exactly as long as the guard had no code to offer, and the same
 * guard raises two more 400s of the same shape (`PLAINTEXT_WRITE_REQUIRED`,
 * `CLIENT_ID_REQUIRED`) — a near-miss on prose would have read as one of those.
 *
 * WHEN IT HAPPENS. A write is sealed at send time from what the device knows,
 * so this is the window where the device does not know yet: the member enrolled
 * on another device a moment ago, or on this one while a write was in the air.
 * It is a state that ENDS — the status read or the key lands — so the write is
 * worth retrying, and retrying it uncounted, exactly as an offline one is.
 */
export const ENCRYPTED_WRITE_REQUIRED_CODE = 'ENCRYPTED_WRITE_REQUIRED';

/**
 * Whether the server refused this write for carrying plaintext from a member it
 * considers enrolled.
 *
 * @param {unknown} error an axios failure from the annotations transport
 * @returns {boolean}
 */
export function isEncryptedWriteRequiredError(error) {
  const response = error?.response;
  return response?.status === 400 && response?.data?.code === ENCRYPTED_WRITE_REQUIRED_CODE;
}

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * One canonical member id from either side of the comparison. `courses-keys`
 * stores `Number(profile.id)` and the transport passes `profile.id` raw, so a
 * strict compare across that boundary would refuse every write the day the
 * profile arrives with a string id.
 *
 * @param {unknown} value
 * @returns {number | null}
 */
function asMemberId(value) {
  if (value === null || value === undefined || value === '') return null;
  const id = Number(value);
  return Number.isFinite(id) ? id : null;
}

/**
 * The content fields, and everything else, in one pass. `undefined` is dropped
 * from both halves: the store writes `startWordIndex: undefined` for a page it
 * could not resolve, and JSON would drop it from the plaintext anyway — doing
 * it here means the padded plaintext and the outgoing body agree. `null` is
 * kept: it is how a nullable field (a second block anchor, a span with no words
 * before it) says "there is nothing here", which is not the same as "leave
 * whatever was here alone".
 *
 * @param {Record<string, unknown>} body
 * @param {readonly string[]} fields
 * @returns {{ plaintext: Record<string, unknown>, rest: Record<string, unknown> }}
 */
function split(body, fields) {
  const plaintext = {};
  const rest = {};
  Object.entries(body).forEach(([key, value]) => {
    if (value === undefined) return;
    // The shell marker is sealed with the row's own fields, whatever the type:
    // in the clear it would be a column the DTO has never heard of.
    if (fields.includes(key) || key === UNRECOVERABLE_FIELD) plaintext[key] = value;
    else rest[key] = value;
  });
  return { plaintext, rest };
}

/** An answer the member has cleared. Whitespace counts as cleared. */
function isEmptyAnswer(answer) {
  return answer === null || answer === undefined || String(answer).trim() === '';
}

const DEFAULT_DEPS = Object.freeze({
  seal,
  tagNameIndex,
  blankItemId,
  randomUuid,
  revisionFor,
  enrolmentInFlight,
  setHooks: setAnnotationTransportHooks,
  now: () => Date.now(),
});

/**
 * What this device already knows, WITHOUT asking the server.
 *
 * Read from three places because any of them can be the one that knows first:
 * holding a master key at all (a device that has one is enrolled by
 * definition — and this is the only arm that answers for a device approved by
 * another of the member's phones, which never sees an enrolment outcome of its
 * own), the device list from `GET /me/encryption`, and the enrolment status a
 * 201 or a 409 wrote locally.
 *
 * ⚠️ EXPORTED FOR THE DRAIN, and the two callers must not drift. `sealRequest`
 * uses it to decide whether a write may go up in the clear; `drainOutbox` uses
 * it to decide whether a queued plaintext body must be sealed before it is
 * sent. If they disagreed, the drain would send in the clear what the hook
 * would have refused, or seal what the hook would have passed through — so
 * there is one function rather than two expressions that happen to match today.
 *
 * @param {object} store
 * @returns {boolean}
 */
export function enrolmentKnown(store) {
  if (store.getters?.['courses-keys/hasMasterKey'] === true) return true;
  if (store.getters?.['courses-devices/enrolled'] === true) return true;
  const status = store.state?.['courses-keys']?.enrolmentStatus;
  return ENROLLED_STATUSES.includes(status);
}

/**
 * One status read, tried twice: the read and the write are two requests, and
 * a network that drops one in ten would otherwise fail the read and pass the
 * write. No delay between the attempts; a write is waiting on this.
 *
 * @param {object} store
 */
async function readStatus(store) {
  let lastError;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      await store.dispatch('courses-devices/refreshStatus');
      return;
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError;
}

/**
 * Whether the API considers this member enrolled, whatever this device holds,
 * reading the status once when nothing here knows yet.
 *
 * `noNetwork` is `pause`'s word (write-volume plan §3.3): it runs inside the few
 * seconds iOS allows a backgrounded app, where a request is a race against the
 * freeze and the app sets no client-side timeout to lose it by. When the status
 * is already known — after the read the Next Steps pages make on entry — this
 * decides without the network exactly as before; when it is not, the answer is
 * "not known" at once rather than after a request that may never return. The
 * caller keeps the row in memory either way, so nothing is lost by refusing.
 *
 * WHY THE READ. The enrolment attempt used to be the only thing that read the
 * status on the write path, so any precondition that skipped it (the flag off
 * for a member who enrolled before it was rolled back, a store that cannot
 * keep a key, a hydration past its budget) left this decision with nothing to
 * go on, and nothing was taken to mean "not enrolled": the write went up in
 * the clear for a member the API then refused. The read is single-flight
 * inside `courses-devices`, and the approval screens reuse its answer.
 *
 * WHY A FAILED READ REFUSES. "What stopped the read stops the write" is only
 * true of a hard outage; on a flaky network the next request can succeed, and
 * the one write that got through would be the plaintext one. So a read that
 * fails is not "not enrolled", it is not known, and the write is refused as
 * STATUS_UNKNOWN. That costs a plaintext member one refused write when the
 * read fails and the write would not have, which is what the backoff and the
 * second attempt keep rare.
 *
 * @param {{ store: object, readState: { failedAt: number }, now: () => number }} wiring
 * @throws {SealedWriteError} STATUS_UNKNOWN
 */
async function apiSaysEnrolled({ store, readState, now, noNetwork = false }) {
  if (enrolmentKnown(store)) return true;
  if (!statusNeverRead(store)) return false;
  if (noNetwork) {
    throw new SealedWriteError(SEALED_WRITE_ERRORS.STATUS_UNKNOWN, 'not read yet, no network here');
  }
  if (readState.failedAt && now() - readState.failedAt < STATUS_READ_BACKOFF_MS) {
    throw new SealedWriteError(SEALED_WRITE_ERRORS.STATUS_UNKNOWN, 'read failed recently');
  }
  try {
    await readStatus(store);
  } catch (error) {
    readState.failedAt = now();
    console.warn('[CoursesE2EE] Could not read the encryption status before a write:', error);
    throw new SealedWriteError(SEALED_WRITE_ERRORS.STATUS_UNKNOWN, String(error?.message ?? error));
  }
  readState.failedAt = 0;
  return enrolmentKnown(store);
}

/**
 * A write that outlived its member is refused before anything is read or
 * sealed: on a shared phone, a keystroke of the member who just signed out
 * must not be decided from, or sent under, the next member's session. Both
 * ids have to be known; a profile that has not loaded is the NO_MEMBER case,
 * and only once sealing is actually needed.
 *
 * @param {object} store
 * @param {{ memberId?: unknown }} context
 */
function assertSameMember(store, context) {
  const contextMemberId = asMemberId(context?.memberId);
  const storeMemberId = asMemberId(store.getters['courses-keys/memberId']);
  if (contextMemberId !== null && storeMemberId !== null && contextMemberId !== storeMemberId) {
    throw new SealedWriteError(
      SEALED_WRITE_ERRORS.MEMBER_MISMATCH,
      `${context.memberId} vs ${storeMemberId}`,
    );
  }
}

/**
 * A note, highlight or tag: seal the content fields, mint an id if this is a
 * create, and add the tag blind index.
 *
 * @param {object} args
 * @param {object} args.deps
 * @param {number} args.memberId
 * @param {{ mk: Uint8Array, keyId: string }} args.material
 * @param {object} args.store the root Vuex store, for the other generation
 * @param {string} args.type
 * @param {string | null} args.itemId
 * @param {Record<string, unknown>} args.body
 */
async function sealRow({ store, deps, memberId, material, type, itemId, body }) {
  const minted = itemId === null || itemId === undefined;
  const rowId = minted ? deps.randomUuid() : itemId;
  const { plaintext, rest } = split(body, SEALED_FIELDS[type]);
  if (Object.keys(plaintext).length === 0) {
    // Sealing `{}` would blank the row's payload; refusing is the honest answer.
    throw new SealedWriteError(SEALED_WRITE_ERRORS.EMPTY_PATCH, `${type} ${rowId}`);
  }
  if (!minted) {
    const missing = REQUIRED_ON_UPDATE[type].filter(field => !(field in plaintext));
    if (missing.length) {
      throw new SealedWriteError(
        SEALED_WRITE_ERRORS.PARTIAL_PATCH,
        `${type} ${rowId} carried no ${missing.join(', ')}`,
      );
    }
  }
  if (type === ANNOTATION_TYPE.TAG && typeof plaintext.name !== 'string') {
    throw new SealedWriteError(SEALED_WRITE_ERRORS.NO_TAG_NAME, `tag ${rowId}`);
  }
  const payload = await deps.seal({
    memberId,
    type,
    itemId: rowId,
    plaintext,
    mk: material.mk,
    keyId: material.keyId,
  });
  const sealed = { ...rest, payload };
  if (minted) sealed.id = rowId;
  else {
    // #1584. An UPDATE replaces the whole payload column, so the API takes it
    // only against the revision this device last read: `PATCH` without
    // `expectedRevision` is a 400, and with a stale one a 409 that changes
    // nothing. A create needs none — the row is born at revision 1.
    //
    // Refused rather than sent blind when this device has not read the row this
    // session. The request would be refused anyway; failing here keeps the
    // member's edit in hand and tells the caller which row to re-open.
    const expectedRevision = deps.revisionFor(type, rowId);
    if (expectedRevision === null) {
      throw new SealedWriteError(SEALED_WRITE_ERRORS.NO_REVISION, `${type} ${rowId}`);
    }
    sealed.expectedRevision = expectedRevision;
  }
  if (type === ANNOTATION_TYPE.TAG) {
    sealed.nameIndex = await deps.tagNameIndex({ mk: material.mk, memberId, name: plaintext.name });
    /**
     * ⚠️ THE SAME NAME UNDER THE OTHER GENERATION, while a rotation is running
     * (#1694, api#1141).
     *
     * The blind index is derived from MK, so one name has two indexes while
     * two generations are live — and the member's tags are split across both,
     * because the walk moves them one at a time while every write goes under
     * the pending one. The server compares indexes and cannot compute either,
     * so a duplicate on the side it was not told about is invisible to it.
     *
     * What that costs is not a duplicate tag. The second "Grace" is accepted
     * under MK2; the walk then re-seals the first under MK2; the two collide
     * on the unique index; that row can never move; and `rotate/commit`
     * refuses for ever on a count that never reaches zero.
     *
     * So both go up whenever there is a second generation to compute, and
     * neither when there is not — which is every write outside a rotation.
     */
    const other = otherGenerationOf(store, material);
    if (other) {
      sealed.altNameIndex = await deps.tagNameIndex({
        mk: other.mk,
        memberId,
        name: plaintext.name,
      });
    }
  }
  return sealed;
}

/**
 * The generation this write is NOT sealed under, or null.
 *
 * Null outside a rotation, and null on a device that holds only one of the two
 * — such a device cannot write at all while a rotation runs (it is refused as
 * ROTATION_IN_PROGRESS), so there is no case where this is missing and a write
 * still goes up.
 */
function otherGenerationOf(store, material) {
  const generations = store.getters?.['courses-keys/readGenerations'];
  if (!Array.isArray(generations)) return null;
  return generations.find(entry => entry?.mk && entry.keyId !== material.keyId) ?? null;
}

/**
 * A blank answer: the composite key is the item id, and an emptied blank asks
 * the seam for a DELETE instead of an envelope (spec 6.4, decision 30), so
 * "answered" stays a fact about rows the leader roster can count without
 * reading anything.
 *
 * The emptied check comes FIRST: the delete is keyed by the same three query
 * parameters the upsert is, so it does not need the composite item id, and
 * deriving that id first turned a cleared answer with an unresolved block into
 * a raw TypeError that blocked the delete as well as the write.
 *
 * @param {object} args
 * @param {object} args.deps
 * @param {number} args.memberId
 * @param {{ mk: Uint8Array, keyId: string }} args.material
 * @param {Record<string, unknown>} args.body
 */
async function sealBlank({ deps, memberId, material, body }) {
  const emptied = isEmptyAnswer(body.answer);
  // The id is needed on BOTH paths now, because a delete names the revision it
  // read too — but a delete must still work without one, which is why this
  // resolves rather than throws for an emptied answer. The order the original
  // comment protects is preserved by the `emptied` check, not by where the id
  // is computed.
  let itemId = null;
  try {
    itemId = deps.blankItemId(body.programSlug, body.lessonSlug, body.blockId);
  } catch (error) {
    // Keep the failure inside the documented set: the composite key asserts its
    // three parts, and a caller that lost one of them is not a crash.
    if (!emptied) {
      throw new SealedWriteError(SEALED_WRITE_ERRORS.NO_BLANK_KEY, String(error?.message ?? error));
    }
  }
  const expectedRevision = itemId ? deps.revisionFor(ANNOTATION_TYPE.BLANK, itemId) : null;
  if (emptied) {
    // A clear is a write like any other, so it is held to the revision it read
    // (decision plan §2.1, amending encryption decision 43). Unconditional when
    // this device has not read the row — there is nothing to be stale about.
    return expectedRevision === null
      ? DELETE_BLANK_ANSWER
      : { ...DELETE_BLANK_ANSWER, expectedRevision };
  }
  const { plaintext, rest } = split(body, SEALED_FIELDS[ANNOTATION_TYPE.BLANK]);
  const payload = await deps.seal({
    memberId,
    type: ANNOTATION_TYPE.BLANK,
    itemId,
    plaintext,
    mk: material.mk,
    keyId: material.keyId,
  });
  // NEVER REQUIRED, unlike a note's. A blank is an upsert: the first answer has
  // no row and so no revision to name, and refusing it would make a member's
  // first write to every blank fail.
  return expectedRevision === null ? { ...rest, payload } : { ...rest, payload, expectedRevision };
}

/**
 * The key material for this write, waiting once for a hydration that has not
 * finished. A cold launch can put the first write ahead of the key store read
 * that App.vue started, and a store that has not been read yet is not a store
 * without keys. Only ever awaited while `courses-keys` says it is hydrating, so
 * a store without the module (most unit specs) and a settled one both skip it.
 *
 * @param {object} store
 */
async function keyMaterialWhenSettled(store) {
  const material = writeGenerationOf(store);
  if (material) return material;
  if (store.getters?.['courses-keys/hydrationStatus'] !== HYDRATING) return null;
  try {
    await store.dispatch('courses-keys/ready', { timeoutMs: HYDRATION_WAIT_MS });
  } catch (error) {
    // A hydration that failed or timed out leaves the decision to the enrolment
    // check below, which is the one that knows whether plaintext is allowed.
  }
  return writeGenerationOf(store) || null;
}

/**
 * The generation this write must be sealed under (#1694).
 *
 * Once a rotation has begun, the API refuses any `kid` but the pending one
 * (api#1133) — so this is not a preference, it is the only value the server
 * will accept, and sealing under the committed generation would lose the write
 * to a 409 the member cannot act on.
 *
 * Falls back to `keyMaterial` for a store without the rotation getters, so a
 * spec that stubs only that one behaves exactly as it did.
 */
function writeGenerationOf(store) {
  const generation = store.getters?.['courses-keys/writeGeneration'];
  if (generation !== undefined) return generation;
  return store.getters['courses-keys/keyMaterial'];
}

/**
 * The types whose plaintext branch moves a revision counter the API checks, and
 * so the only ones worth naming one on. Highlights and tags stay out: the engine
 * does not write them, and their plaintext branches do not move the counter, so
 * a revision there would name a number the server never reads.
 */
const REVISION_CARRIERS = new Set([ANNOTATION_TYPE.NOTE, ANNOTATION_TYPE.BLANK]);

/**
 * A plaintext note or blank UPDATE, with the revision this device last read the
 * row at. Never required: with no revision in hand (a create has no row; a row
 * not read this session) the body passes byte for byte, which is what it always
 * did.
 *
 * A blank derives its id from the BODY rather than the context, because the
 * upsert is keyed on `program/lesson/block` and has no row id to be addressed
 * by. That is also why an un-enrolled member's cleared blank comes through here
 * at all: for them an emptied answer is a PUT of `''`, not the delete an
 * enrolled member's is, so it is conditional in the same way every other write
 * of theirs is.
 */
function plaintextRevision(deps, context, body) {
  if (!REVISION_CARRIERS.has(context?.type) || !isPlainObject(body)) return body;
  let itemId = null;
  if (context.type === ANNOTATION_TYPE.BLANK) {
    try {
      itemId = deps.blankItemId(body.programSlug, body.lessonSlug, body.blockId);
    } catch (error) {
      // An incomplete key is not a reason to refuse a plaintext write that would
      // otherwise have gone; it only means there is no revision to name.
      return body;
    }
  } else {
    // `!context.itemId` is a create, which has no row and must never name a
    // revision — checked here rather than left to `revisionFor`, whose injected
    // stand-ins answer for any id.
    itemId = context.itemId;
  }
  if (!itemId) return body;
  const expectedRevision = deps.revisionFor(context.type, itemId);
  return expectedRevision === null ? body : { ...body, expectedRevision };
}

/**
 * The key this device is about to hold, if an enrolment is finishing right now.
 *
 * Null whenever nothing is in flight, which is every ordinary NO_KEY: a second
 * device awaiting approval, a revoked one, a persist that failed. Those refuse
 * at once, exactly as they did.
 *
 * @returns {Promise<{ mk: Uint8Array, keyId: string } | null>}
 */
async function keyFromEnrolmentInFlight({ store, deps, context }) {
  const running = deps.enrolmentInFlight(store.getters?.['courses-keys/memberId']);
  if (!running) return null;
  // It answers expected failures as outcomes; an unexpected throw is not a
  // reason to lose the member's words, only to refuse this write as NO_KEY.
  await running.catch(() => {});
  // The enrolment may have outlived a sign-out, exactly as the status read can.
  assertSameMember(store, context);
  return keyMaterialWhenSettled(store);
}

/**
 * The request hook itself.
 *
 * @param {{ store: object, deps: object, readState: { failedAt: number } }} wiring
 * @param {{ memberId: number | null, type: string, itemId: string | null }} context
 * @param {unknown} body
 * @param {{ noNetwork?: boolean }} [options] only `sealAnnotation` passes one
 */
async function sealRequest({ store, deps, readState }, context, body, { noNetwork = false } = {}) {
  assertSameMember(store, context);
  let material = await keyMaterialWhenSettled(store);
  if (!material) {
    // A rotation is running and this device is not the one running it. It may
    // hold MK1 and read every row it always could; it simply cannot write one
    // until the rotation ends, and the member's other phones going read-only
    // is what spec 6.12 describes rather than a fault. Distinguished from
    // NO_KEY so the screen can say which it is.
    if (store.getters?.['courses-keys/rotationInProgress']) {
      throw new SealedWriteError(SEALED_WRITE_ERRORS.ROTATION_IN_PROGRESS, context?.type);
    }
    // Enrolled with no key here: the API refuses plaintext beside a payload, so
    // sending it loses the write AND puts the member's words on the wire.
    const enrolled = await apiSaysEnrolled({ store, readState, now: deps.now, noNetwork });
    // The read may have outlived a sign-out.
    assertSameMember(store, context);
    if (enrolled) {
      // ONE STATE IS NOT "NO KEY HERE", IT IS "NOT YET". Between the server
      // accepting this device's enrol POST and this device promoting its key,
      // the member is enrolled and this device holds nothing — the same shape
      // as a second, unapproved device, and until now the same refusal. But
      // NO_KEY drops the slot and rolls a blank answer back, so the member who
      // enrolled BY TYPING lost the words that enrolled them.
      //
      // Waiting is bounded by the enrolment, which settles to an outcome rather
      // than hanging, and every other NO_KEY case is untouched: a device with
      // nothing in flight still refuses at once. Type-agnostic, so it also
      // covers a note typed in the Hub while the Hub-open enrolment finishes.
      material = await keyFromEnrolmentInFlight({ store, deps, context });
      if (!material) throw new SealedWriteError(SEALED_WRITE_ERRORS.NO_KEY, context?.type);
    } else {
      // Genuinely not enrolled: plaintext, as before this module.
      return plaintextRevision(deps, context, body);
    }
  }
  if (!isPlainObject(body)) return body;

  const type = context?.type;
  if (!SEALED_FIELDS[type]) throw new SealedWriteError(SEALED_WRITE_ERRORS.UNKNOWN_TYPE, type);

  const memberId = store.getters['courses-keys/memberId'];
  // The AAD is built from the TRANSPORT's member id, because that is the one
  // the read path will use to open the row. A null one would bind the row to
  // "null:note:<id>" for ever, and a mismatch would bind it to a member who
  // cannot read it: refuse both rather than write something unopenable.
  const contextMemberId = asMemberId(context?.memberId);
  if (contextMemberId === null) {
    throw new SealedWriteError(SEALED_WRITE_ERRORS.NO_MEMBER, type);
  }
  // Checked again after the hydration wait, the same way as above.
  assertSameMember(store, context);

  if (type === ANNOTATION_TYPE.BLANK) return sealBlank({ deps, memberId, material, body });
  return sealRow({ store, deps, memberId, material, type, itemId: context.itemId, body });
}

/**
 * Attach sealing to the annotations seam. Called once at start-up (App.vue),
 * not at enrolment: after a reload the hook has to be in place before the first
 * write, and it is a pass-through until `courses-keys` holds a key.
 *
 * @param {object} args
 * @param {object} args.store the root Vuex store
 * @param {object} [args.deps] injected for tests; the app passes none
 * @returns {(context: object, body: unknown) => Promise<unknown>} the installed hook
 */
export function installSealedWrites({ store, deps: overrides } = {}) {
  if (!store) throw new TypeError('installSealedWrites needs the root Vuex store');
  const deps = overrides ? { ...DEFAULT_DEPS, ...overrides } : DEFAULT_DEPS;
  // When the last status read failed, for the backoff. One per installation.
  const readState = { failedAt: 0 };
  const request = (context, body, options) =>
    sealRequest({ store, deps, readState }, context, body, options);
  deps.setHooks({ request });
  return request;
}
